import {useQuery} from '@tanstack/react-query';
import React from 'react';

import {getAuthHeaders} from 'app/utils/apiUtils';
import {formatTilerThumbnailUrl, isTilerUrl} from 'app/utils/mapUtils';

export type Props = {
  // A tiler thumbnail URL (e.g. .../cog/thumbnail.png?...). Auth/context params
  // are stripped and sent as headers instead.
  url: string;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
  // Defers the fetch while false, e.g. until the thumbnail scrolls into view.
  enabled?: boolean;
  // Fires when the rendered <img> finishes loading.
  onLoad?: () => void;
  // Fires when the thumbnail can't be shown (non-tiler URL, fetch failure, non-2xx, or 404).
  onError?: () => void;
};

/**
 * Renders a tiler thumbnail by fetching the PNG with an `Authorization` header
 * (rather than an `apiToken` query param) and displaying the result as a blob
 * object-URL. Because the fetched URL carries no per-user/per-org query params,
 * Cloudflare can serve a single cached response to every user instead of
 * missing the cache on each unique token.
 *
 * This works for thumbnails because they're one-off images fetched in JS; map
 * tiles loaded as <img>/Leaflet can't set request headers and stay on
 * `formatTileUrl`'s query-param path.
 */
const TilerThumbnail: React.FunctionComponent<Props> = ({
  url,
  className,
  style,
  alt,
  enabled = true,
  onLoad,
  onError,
}) => {
  const isTiler = isTilerUrl(url);
  const cleanUrl = React.useMemo(() => formatTilerThumbnailUrl(url), [url]);

  // Hold the latest onError without making it a dependency below: it's typically
  // an inline arrow (new reference every parent render), and depending on it
  // would re-fire the error effect on every re-render once the query has settled.
  const onErrorRef = React.useRef(onError);
  React.useLayoutEffect(() => {
    onErrorRef.current = onError;
  });

  const {data: blob, isError, isSuccess} = useQuery({
    queryKey: ['tilerThumbnail', cleanUrl],
    // Only fetch (and only attach auth headers) for our own tiler URLs.
    enabled: isTiler && enabled,
    queryFn: async (): Promise<Blob | null> => {
      const headers = await getAuthHeaders();
      const response = await fetch(cleanUrl, {headers});
      // A missing thumbnail (no imagery) is an expected, non-error outcome.
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`Failed to load thumbnail: ${response.status} ${response.statusText}`);
      }
      return response.blob();
    },
  });

  // Create the object URL in an effect (not useMemo) so create/revoke stay paired:
  // in Strict Mode React discards the first useMemo result, which would leak the
  // URL it created. Keeping both sides in one effect guarantees every created URL
  // is revoked when the blob changes or the component unmounts.
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  // Signal "can't show a thumbnail" on a non-tiler URL (query is disabled), a
  // hard error, or a 404 (queryFn resolves to null). None of these mount an
  // <img>, so without this the consumer's onLoad would never fire and a
  // loading placeholder would spin forever.
  React.useEffect(() => {
    if (!isTiler || isError || (isSuccess && !blob)) {
      onErrorRef.current?.();
    }
  }, [isTiler, isError, isSuccess, blob]);

  if (!objectUrl) {
    return null;
  }

  return (
    <img
      className={className}
      style={style}
      alt={alt}
      src={objectUrl}
      onLoad={() => onLoad?.()}
    />
  );
};

export default TilerThumbnail;
